import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  FileText,
  Cloud,
  Upload,
  Download,
  RefreshCw,
  Search,
  Grid,
  List,
  MoreVertical,
  Star,
  StarOff,
  Trash2,
  ExternalLink,
  LayoutTemplate,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { supabase } from '@/integrations/supabase/client';
import { triggerIntegrationSync } from '@/lib/integrationSync';
import { formatDistanceToNow } from 'date-fns';
import TemplatesTab from './TemplatesTab';
import { useUserRole } from '@/hooks/useUserRole';
import DocumentViewerModal, { type ProjectDocument } from './DocumentViewerModal';
import { useLocalization } from '@/hooks/useLocalization';
import { runDocumentImport } from '@/lib/documentImport';

const DOCS_BUCKET = 'project-documents';

const mimeToType = (mime: string | null): 'doc' | 'spreadsheet' | 'presentation' | 'pdf' | 'folder' => {
  if (!mime) return 'doc';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return 'spreadsheet';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'presentation';
  return 'doc';
};

const formatSize = (bytes: number | null): string => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface OneDriveIntegration {
  id: string;
  is_active: boolean;
  last_sync_at: string | null;
}

interface DocumentsViewProps {
  // Navigates to the Document Intelligence review view (an activeView
  // within Index.tsx's renderContent switch, not a router route) — mirrors
  // the onNavigateToActionHub/onNavigateToGovernance callback-prop
  // convention already used by MeetingHub/ActionHub.
  onNavigateToDocumentIntelligence?: () => void;
}

const DocumentsView = ({ onNavigateToDocumentIntelligence }: DocumentsViewProps) => {
  const { t } = useLocalization();
  const { user, tenantId } = useAuth();
  const { isAdmin } = useUserRole();
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string | 'all'>('all');
  const [syncingAll, setSyncingAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<ProjectDocument | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'templates'>('documents');
  const [oneDrive, setOneDrive] = useState<OneDriveIntegration | null>(null);
  const [oneDriveLoading, setOneDriveLoading] = useState(true);
  const [suggestedCandidateCount, setSuggestedCandidateCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    const { data, error } = await supabase
      .from('project_documents')
      .select('id, name, file_path, mime_type, size_bytes, starred, created_at, project_id, summary, summary_generated_at, projects_sync(name)')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setDocuments(
        data.map((d) => ({
          id: d.id,
          name: d.name,
          file_path: d.file_path,
          mime_type: d.mime_type,
          size_bytes: d.size_bytes,
          starred: d.starred,
          created_at: d.created_at,
          project_id: d.project_id,
          project_name: (d.projects_sync as { name: string } | null)?.name ?? null,
          summary: d.summary,
          summary_generated_at: d.summary_generated_at,
        }))
      );
    }
    setDocumentsLoading(false);
  }, []);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const loadOneDriveIntegration = useCallback(async () => {
    setOneDriveLoading(true);
    const { data, error } = await supabase
      .from('integration_configs')
      .select('id, is_active, last_sync_at')
      .eq('integration_type', 'onedrive_documents')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!error) {
      setOneDrive(data as OneDriveIntegration | null);
    }
    setOneDriveLoading(false);
  }, []);

  const loadSuggestedCandidateCount = useCallback(async () => {
    const { count, error } = await supabase
      .from('document_candidates')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'suggested');
    if (!error) {
      setSuggestedCandidateCount(count ?? 0);
    }
  }, []);

  useEffect(() => { loadOneDriveIntegration(); }, [loadOneDriveIntegration]);
  useEffect(() => { loadSuggestedCandidateCount(); }, [loadSuggestedCandidateCount]);

  const projects = [...new Set(documents.map((d) => d.project_name).filter((p): p is string => !!p))];

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = selectedProject === 'all' || doc.project_name === selectedProject;
    return matchesSearch && matchesProject;
  });

  const getTypeIcon = (mime: string | null) => {
    switch (mimeToType(mime)) {
      case 'spreadsheet':
        return <FileText className="w-5 h-5 text-success" />;
      case 'presentation':
        return <FileText className="w-5 h-5 text-warning" />;
      case 'pdf':
        return <FileText className="w-5 h-5 text-destructive" />;
      default:
        return <FileText className="w-5 h-5 text-primary" />;
    }
  };

  const handleSyncAll = async () => {
    if (!oneDrive || !oneDrive.is_active) {
      toast({
        title: t('OneDrive not connected'),
        description: t('Connect OneDrive from Integrations before syncing documents.'),
        variant: 'destructive',
      });
      return;
    }

    setSyncingAll(true);
    toast({ title: t('Syncing'), description: t('Syncing files from OneDrive...') });
    try {
      await triggerIntegrationSync(oneDrive.id, 'onedrive_documents');
      await Promise.all([loadDocuments(), loadOneDriveIntegration()]);

      // Extraction (above) only pulls file text into synced_documents; it does
      // not itself produce document_candidates, and the scheduled analysis can
      // be an hour away. Run the import now: it reads every waiting file,
      // grouping the ones that belong to one project (see @/lib/documentImport).
      toast({ title: t('Analyzing'), description: t('Reading your documents...') });
      try {
        const result = await runDocumentImport();
        toast({
          title: t('Sync Complete'),
          description: result.nothingToDo
            ? t('All documents are up to date.')
            : result.remaining === 0
              ? t('{0} project(s) found in your documents.', { 0: result.projectsWritten })
              : t('{0} project(s) found; {1} item(s) will be retried on the next sync.', { 0: result.projectsWritten, 1: result.remaining }),
          variant: !result.nothingToDo && result.processed === 0 ? 'destructive' : undefined,
        });
      } catch (analyzeErr) {
        // Extraction still succeeded; only the analysis phase couldn't be
        // kicked off. Say so rather than implying full completion.
        console.error('run-document-import failed:', analyzeErr);
        toast({
          title: t('Sync Complete'),
          description: t('Files synced, but could not check for documents to analyze.'),
        });
      }

      await loadSuggestedCandidateCount();
    } catch (err) {
      toast({
        title: t('Sync failed'),
        description: err instanceof Error ? err.message : t('Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleConnectOneDrive = () => {
    navigate(tenantSlug ? `/${tenantSlug}/integrations?tab=candidates` : '/integrations?tab=candidates');
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file || !user) return;

    setUploading(true);
    try {
      const path = `${user.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage.from(DOCS_BUCKET).upload(path, file);
      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase.from('project_documents').insert({
        user_id: user.id,
        tenant_id: tenantId,
        name: file.name,
        file_path: path,
        mime_type: file.type || null,
        size_bytes: file.size,
      });
      if (insertError) throw insertError;

      toast({ title: t('Document uploaded'), description: file.name });
      loadDocuments();
    } catch (err) {
      toast({
        title: t('Upload failed'),
        description: err instanceof Error ? err.message : t('Unknown error'),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage.from(DOCS_BUCKET).createSignedUrl(doc.file_path, 60);
    if (error || !data) {
      toast({ title: t('Could not open document'), description: error?.message, variant: 'destructive' });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = async (doc: ProjectDocument) => {
    const { data, error } = await supabase.storage.from(DOCS_BUCKET).createSignedUrl(doc.file_path, 60, { download: doc.name });
    if (error || !data) {
      toast({ title: t('Could not download document'), description: error?.message, variant: 'destructive' });
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteDocument = async (doc: ProjectDocument) => {
    const { error: storageError } = await supabase.storage.from(DOCS_BUCKET).remove([doc.file_path]);
    if (storageError) {
      toast({ title: t('Delete failed'), description: storageError.message, variant: 'destructive' });
      return;
    }
    const { error: dbError } = await supabase.from('project_documents').delete().eq('id', doc.id);
    if (dbError) {
      toast({ title: t('Delete failed'), description: dbError.message, variant: 'destructive' });
      return;
    }
    toast({ title: t('Document deleted') });
    setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
  };

  const toggleStar = async (doc: ProjectDocument) => {
    setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, starred: !d.starred } : d)));
    const { error } = await supabase.from('project_documents').update({ starred: !doc.starred }).eq('id', doc.id);
    if (error) {
      // revert on failure
      setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, starred: doc.starred } : d)));
      toast({ title: t('Could not update star'), description: error.message, variant: 'destructive' });
    }
  };

  const handleSummaryGenerated = (docId: string, summary: string, summaryGeneratedAt: string) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === docId ? { ...d, summary, summary_generated_at: summaryGeneratedAt } : d))
    );
    setViewerDoc((prev) => (prev && prev.id === docId ? { ...prev, summary, summary_generated_at: summaryGeneratedAt } : prev));
  };

  return (
    <div className="space-y-6">
      <div className="masira-fade-in">
        <h2 className="text-2xl font-bold text-foreground">{t('Documents')}</h2>
        <p className="text-muted-foreground">{t('Project files synced with OneDrive')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'documents' | 'templates')} className="w-full">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="documents" className="data-[state=active]:bg-background">
            <FileText className="w-4 h-4 me-2" />
            
            {t('Documents')}
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-background">
            <LayoutTemplate className="w-4 h-4 me-2" />
            
            {t('Templates')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-6 mt-6">
          {/* Sync Status Bar */}
          <div className="masira-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#0078D4]/10 flex items-center justify-center">
                    <Cloud className="w-4 h-4 text-[#0078D4]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('OneDrive')}</p>
                    {oneDriveLoading ? (
                      <p className="text-xs text-muted-foreground">{t('Checking...')}</p>
                    ) : oneDrive?.is_active ? (
                      <p className="text-xs text-success">
                        
                        {t('Connected')}
                        {oneDrive.last_sync_at && (
                          <span className="text-muted-foreground">
                            {' '}{t('· Last synced')} {formatDistanceToNow(new Date(oneDrive.last_sync_at), { addSuffix: true })}
                          </span>
                        )}
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground">{t('Not connected')}</p>
                        <button
                          onClick={handleConnectOneDrive}
                          className="text-xs text-primary hover:underline"
                        >
                          
                          {t('Connect OneDrive')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncingAll || oneDriveLoading}>
                  <RefreshCw className={cn('w-4 h-4 me-2', syncingAll && 'animate-spin')} />
                  {syncingAll ? t('Syncing...') : t('Sync All')}
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />
                <Button size="sm" onClick={handleUploadClick} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Upload className="w-4 h-4 me-2" />}
                  {uploading ? t('Uploading...') : t('Upload')}
                </Button>
              </div>
            </div>
            {suggestedCandidateCount > 0 && (
              <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                <p className="text-xs text-foreground">
                  {suggestedCandidateCount}  {t('detected project')}{suggestedCandidateCount === 1 ? '' : 's'}  {t('awaiting review')}
                </p>
                <button
                  onClick={() =>
                    isAdmin
                      ? navigate(tenantSlug ? `/${tenantSlug}/integrations` : '/integrations')
                      : onNavigateToDocumentIntelligence?.()
                  }
                  className="text-xs font-medium text-primary hover:underline"
                >
                  
                  {t('Review detected projects')}
                </button>
              </div>
            )}
          </div>

          {/* Filters & Search */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('Search documents...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="ps-10"
                />
              </div>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="all">{t('All Projects')}</option>
                {projects.map((project) => (
                  <option key={project} value={project}>
                    {project}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-secondary/50">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'grid' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                )}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 rounded-md transition-colors',
                  viewMode === 'list' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Documents Grid/List */}
          {documentsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <FileText className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm">{t('No documents yet. Upload one to get started.')}</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDocuments.map((doc, index) => (
                <div
                  key={doc.id}
                  onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}
                  className="masira-card p-4 hover:border-primary/30 transition-all cursor-pointer masira-slide-up group"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(doc.mime_type)}
                    </div>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleStar(doc)} className="text-muted-foreground hover:text-warning">
                        {doc.starred ? (
                          <Star className="w-4 h-4 fill-warning text-warning" />
                        ) : (
                          <StarOff className="w-4 h-4" />
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover">
                          <DropdownMenuItem onClick={() => handleOpenDocument(doc)}>
                            <ExternalLink className="w-4 h-4 me-2" />
                            
                            {t('Open')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                            <Download className="w-4 h-4 me-2" />
                            
                            {t('Download')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteDocument(doc)}>
                            <Trash2 className="w-4 h-4 me-2" />
                            
                            {t('Delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <h4 className="font-medium text-foreground truncate mb-1">{doc.name}</h4>
                  <p className="text-xs text-muted-foreground mb-3">{doc.project_name ?? t('Unfiled')}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{formatSize(doc.size_bytes)}</span>
                    <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="nexus-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">{t('Name')}</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">{t('Project')}</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">{t('Modified')}</th>
                    <th className="text-start p-4 text-sm font-medium text-muted-foreground">{t('Size')}</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      onClick={() => { setViewerDoc(doc); setViewerOpen(true); }}
                      className="border-b border-border hover:bg-secondary/30 transition-colors group cursor-pointer"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <button onClick={(e) => { e.stopPropagation(); toggleStar(doc); }} className="text-muted-foreground hover:text-warning">
                            {doc.starred ? (
                              <Star className="w-4 h-4 fill-warning text-warning" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </button>
                          {getTypeIcon(doc.mime_type)}
                          <span className="font-medium text-foreground">{doc.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{doc.project_name ?? t('Unfiled')}</td>
                      <td className="p-4 text-sm text-muted-foreground">{formatDistanceToNow(new Date(doc.created_at), { addSuffix: true })}</td>
                      <td className="p-4 text-sm text-muted-foreground">{formatSize(doc.size_bytes)}</td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover">
                            <DropdownMenuItem onClick={() => handleOpenDocument(doc)}>
                              <ExternalLink className="w-4 h-4 me-2" />
                              
                              {t('Open')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadDocument(doc)}>
                              <Download className="w-4 h-4 me-2" />
                              
                              {t('Download')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteDocument(doc)}>
                              <Trash2 className="w-4 h-4 me-2" />
                              
                              {t('Delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <TemplatesTab canManageAll={isAdmin} onDocumentCreated={loadDocuments} />
        </TabsContent>
      </Tabs>

      <DocumentViewerModal
        document={viewerDoc}
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        onOpenOriginal={handleOpenDocument}
        onDownload={handleDownloadDocument}
        onDelete={(doc) => { handleDeleteDocument(doc); setViewerOpen(false); }}
        onBrowseTemplates={() => { setViewerOpen(false); setActiveTab('templates'); }}
        onSummaryGenerated={handleSummaryGenerated}
      />
    </div>
  );
};

export default DocumentsView;
